/**
 * Bootstrap sync — calls GET /api/v1/field/sync/me and hydrates Dexie
 * with the user-scoped envelope. Run once after unlock; the sync
 * engine (Phase 3.19) calls /sync/delta on a schedule from there.
 *
 * Etag round-trip: if we already have a cached etag and the server
 * responds 304, we keep the local data. Otherwise we wipe the cached_*
 * tables for this user and rewrite from the response.
 */
import type {
  AssignedEventEnvelope,
  AssignedSiteEnvelope,
  FieldSyncMeResponse,
} from '@argos/api-types';
import { apiClient, HttpError } from '../api/client';
import { getDb } from '../db';

const ETAG_KEY = 'sync_state';

interface BootstrapResult {
  ok: boolean;
  cached: boolean; // true on 304 (no transfer), false on 200 fresh
  error?: string;
  payload?: FieldSyncMeResponse;
}

export async function runBootstrapSync(): Promise<BootstrapResult> {
  const db = getDb();
  const sync = await db.sync_state.get(ETAG_KEY);
  const lastEtag = sync?.last_etag ?? null;

  let resp: FieldSyncMeResponse & { _etag?: string };
  try {
    // We pass etag via a query param fallback since the bare fetch
    // wrapper doesn't expose response headers easily. Server respects
    // If-None-Match — we set it via a custom header path that
    // generate the same comparison server-side.
    const headers: Record<string, string> = {};
    if (lastEtag) headers['If-None-Match'] = lastEtag;
    resp = await apiClient.getJson<FieldSyncMeResponse & { _etag?: string }>(
      '/api/v1/field/sync/me',
      { headers },
    );
  } catch (err) {
    if (err instanceof HttpError && err.status === 304) {
      // Cache is current.
      return { ok: true, cached: true };
    }
    return { ok: false, cached: false, error: (err as Error).message };
  }

  // Hydrate Dexie. Deliberately NOT one mega-transaction: a failure in
  // one slice (data drift, a non-cloneable value, a Dexie premature
  // commit) must not roll back the rest and leave the device atomically
  // stale. Each section is independent; any failure is recorded, not
  // thrown — a partially-fresh cache beats a stale one, and a sync error
  // must never be invisible.
  const errors: string[] = [];
  const cachedAt = Date.now();

  // Sites + wells (+ recent-data dedup hints).
  try {
    await db.cached_sites.clear();
    await db.cached_wells.clear();
    await db.cached_production_data.clear();
  } catch (e) {
    errors.push(`clear sites: ${(e as Error).message}`);
  }
  for (const env of resp.assigned_sites ?? []) {
    try {
      await hydrateSite(env, cachedAt);
    } catch (e) {
      const id = (env as { site?: { id?: string } }).site?.id ?? '?';
      errors.push(`site ${id}: ${(e as Error).message}`);
      console.error('[field-sync] hydrateSite failed', id, e);
    }
  }

  // Events + plan items + existing field records.
  try {
    await db.cached_events.clear();
    await db.cached_plan_items.clear();
    await db.cached_field_records.clear();
  } catch (e) {
    errors.push(`clear events: ${(e as Error).message}`);
  }
  for (const env of resp.assigned_events ?? []) {
    try {
      await hydrateEvent(env, cachedAt);
    } catch (e) {
      const id = (env as { event?: { id?: string } }).event?.id ?? '?';
      errors.push(`event ${id}: ${(e as Error).message}`);
      console.error('[field-sync] hydrateEvent failed', id, e);
    }
  }

  // Analytes.
  try {
    await db.cached_analytes.clear();
    for (const a of resp.analytes ?? []) {
      const id = (a as { id?: string }).id;
      if (id) await db.cached_analytes.put({ analyte_id: id, data: a });
    }
  } catch (e) {
    errors.push(`analytes: ${(e as Error).message}`);
    console.error('[field-sync] analytes failed', e);
  }

  // Validation rules.
  try {
    await db.cached_validation_rules.clear();
    for (const rule of resp.validation_rules ?? []) {
      if (rule.id) await db.cached_validation_rules.put({ rule_id: rule.id, data: rule });
    }
  } catch (e) {
    errors.push(`rules: ${(e as Error).message}`);
    console.error('[field-sync] rules failed', e);
  }

  // Flag codes (tenant gauging whitelist — wizard quick-actions).
  try {
    await db.cached_flag_codes.clear();
    for (const fc of resp.flag_codes ?? []) {
      if (fc.code) await db.cached_flag_codes.put({ code: fc.code, description: fc.description });
    }
  } catch (e) {
    errors.push(`flag_codes: ${(e as Error).message}`);
    console.error('[field-sync] flag_codes failed', e);
  }

  // Form schemas.
  try {
    await db.cached_form_schemas.clear();
    for (const schema of resp.form_schemas ?? []) {
      const fid = (schema as { formId?: string }).formId;
      if (fid) {
        await db.cached_form_schemas.put({
          formId: fid,
          schemaVersion: (schema as { schemaVersion?: number }).schemaVersion ?? 1,
          data: schema,
        });
      }
    }
  } catch (e) {
    errors.push(`schemas: ${(e as Error).message}`);
    console.error('[field-sync] schemas failed', e);
  }

  try {
    await db.sync_state.put({
      key: 'sync_state',
      last_sync_at: Date.now(),
      last_etag: resp._etag ?? lastEtag,
      last_error: errors.length ? errors.join('; ') : null,
    });
  } catch {
    /* sync_state write is best-effort */
  }

  if (errors.length) {
    return { ok: false, cached: false, error: errors.join('; '), payload: resp };
  }
  return { ok: true, cached: false, payload: resp };
}

async function hydrateSite(
  env: AssignedSiteEnvelope,
  cachedAt: number,
): Promise<void> {
  const db = getDb();
  await db.cached_sites.put({
    site_id: env.site.id,
    data: env.site,
    cached_at: cachedAt,
  });
  for (const w of env.wells) {
    await db.cached_wells.put({
      site_well_key: `${env.site.id}|${w.normalized_name}`,
      site_id: env.site.id,
      well_name: w.name,
      data: w,
    });
  }
  // recent_data slices feed the client-side dedup heuristic.
  for (const g of env.recent_data?.well_gaugings ?? []) {
    if (!g.well_id || !g.gauge_date) continue;
    const key = `${env.site.id}|${g.well_name}|${g.gauge_date}|`;
    await db.cached_production_data.put({
      dedup_key: key,
      site_id: env.site.id,
      well_name: g.well_name,
      sample_date: g.gauge_date,
      compound: null,
      kind: 'gauging',
    });
  }
  for (const a of env.recent_data?.analytical_results ?? []) {
    if (!a.well_id || !a.sample_date) continue;
    const key = `${env.site.id}|${a.well_name}|${a.sample_date}|${a.compound ?? ''}`;
    await db.cached_production_data.put({
      dedup_key: key,
      site_id: env.site.id,
      well_name: a.well_name,
      sample_date: a.sample_date,
      compound: a.compound,
      kind: 'analytical',
    });
  }
}

async function hydrateEvent(
  env: AssignedEventEnvelope,
  cachedAt: number,
): Promise<void> {
  const db = getDb();
  await db.cached_events.put({
    event_id: env.event.id,
    site_id: env.event.site_id,
    status: env.event.status,
    data: env,
    cached_at: cachedAt,
  });
  for (const item of env.plan_items) {
    await db.cached_plan_items.put({
      event_well_key: `${env.event.id}|${item.well_id}`,
      event_id: env.event.id,
      well_id: item.well_id,
      data: item,
    });
  }
  for (const fr of env.existing_field_records ?? []) {
    await db.cached_field_records.put({
      event_well_key: `${env.event.id}|${fr.well_id}`,
      event_id: env.event.id,
      well_id: fr.well_id,
      data: fr,
    });
  }
}
